import { useCallback, useEffect, useRef, useState, type ReactNode } from "react"
import { Link, useParams } from "react-router-dom"
import { AlertTriangle, ArrowLeft, Check, CheckCircle2, Info, Loader2, MoreHorizontal, RefreshCw, Save, ScanBarcode, Trash2, X } from "lucide-react"
import type { AppShell } from "@/App"
import { Scanner, type ScanFeedback } from "@/components/scanner/Scanner"
import { classifyCode, nearMatch } from "@/components/scanner/classify"
import { toRequestError } from "./request-error"
import { usePendingQueue } from "./pending"
import { ActionBar, ActionBarSpacer, BottomSheet, Segmented, useMinWidth } from "./ui"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import {
  checkBox,
  completeReceipt,
  confirmStep,
  createPurchaseOrder,
  getOrderPlan,
  discardReceipt,
  documentTypeLabel,
  eurFmt as eur,
  getReceipt,
  getSerialActivation,
  originLabel,
  patchReceipt,
  pendingStock,
  billTermsText,
  ptDate,
  searchCatalog,
  productCategories,
  createLineProduct,
  type NewProductInput,
  searchSuppliers,
  supplierSource,
  STATUS_LABEL,
  STATUS_VARIANT,
  type BoxChecks,
  type ConfirmStepInfo,
  type CreatedOrder,
  type LineKind,
  type OrderCreation,
  type OrderPlan,
  type ReceiptDetail,
  type ReviewIssue,
  type SupplierOption,
} from "./api"
import { DocumentPreview } from "./DocumentPreview"
import { refreshDecision } from "./order-refresh"
import { SerialActivationDialog, type SerialActivationAsk } from "./SerialActivationDialog"
import { ChargesBlock, LinesCards, LinesTable } from "./ReceiptLines"
import { BeforeConfirmCard, BoxCheckCard, CardTitle, OriginCard, Panel, ValidationCard, WillWriteCard } from "./ValidationPanel"
import { confirmDialog } from "@/lib/confirm"

/** Avisos que ninguém pode dar por vistos: o documento está errado, não a leitura. */
const NOT_DISMISSABLE = ["document_cancelled", "multiple_documents", "order_received"]

type Tab = "linhas" | "documento" | "validacao" | "resumo"

/**
 * Detalhe de uma receção (Fase 2d).
 *
 * ≥ 1280 px: três colunas — documento · formulário (fornecedor, documento,
 * linhas) · validação, fixa ao rolar. 1024–1280: documento e formulário lado
 * a lado, a validação em cartões compactos por cima do formulário. < 1024:
 * separadores «Linhas | Documento | Validação | Resumo». No telemóvel as
 * ações vão para a barra fixa em baixo (a principal à vista, as outras em «⋯»).
 */
export function ReceiptDetailPage({ shell }: { shell: AppShell }) {
  const { id = "" } = useParams()
  const [detail, setDetail] = useState<ReceiptDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const polls = useRef(0)

  const load = useCallback(() => getReceipt(shell, id).then(setDetail).catch((e: Error) => setError(e.message)), [shell, id])

  useEffect(() => {
    load()
  }, [load])

  // A ler: 40–90 s por fatura. Pergunta com recuo (2 s → 5 s → 10 s → 30 s).
  useEffect(() => {
    if (detail?.receipt.status !== "a_extrair") {
      polls.current = 0
      return
    }
    const delays = [2000, 5000, 10000, 30000]
    const t = setTimeout(() => {
      polls.current++
      load()
    }, delays[Math.min(polls.current, delays.length - 1)])
    return () => clearTimeout(t)
  }, [detail, load])

  const patch = async (body: Record<string, unknown>) => {
    setBusy(true)
    setError(null)
    try {
      setDetail(await patchReceipt(shell, id, body))
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  /** «Não é um artigo…» / «desfazer» (0.41.0): a linha muda na mesma; diz-se se a regra do fornecedor não ficou guardada. */
  const patchKind = async (lineIndex: number, kind: LineKind | null) => {
    setBusy(true)
    setError(null)
    try {
      const d = await patchReceipt(shell, id, { line: { line_index: lineIndex, kind } })
      setDetail(d)
      if (d.line_kind_rule && !d.line_kind_rule.saved) {
        shell.bridge.toast({ variant: "error", title: "A linha ficou marcada, mas não ficou aprendida", description: d.line_kind_rule.error ?? undefined })
      }
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const [steps, setSteps] = useState<ConfirmStepInfo[] | null>(null)
  // O progresso é desta receção: ao abrir outra, volta a vir do detalhe.
  useEffect(() => setSteps(null), [id])

  /**
   * O aviso «ativar o número de série» (0.35.0), um artigo de cada vez:
   * `queue` são os artigos que faltam decidir; `fromConfirm` — aberto pelo
   * botão de confirmar, que continua sozinho quando tudo estiver decidido.
   */
  const [activation, setActivation] = useState<{ ask: SerialActivationAsk; queue: number[]; fromConfirm: boolean } | null>(null)
  const [activationBusy, setActivationBusy] = useState(false)
  const [activationError, setActivationError] = useState<string | null>(null)
  const openActivation = async (productId: number, queue: number[], fromConfirm: boolean): Promise<void> => {
    setError(null)
    setActivationError(null)
    try {
      const ask = await getSerialActivation(shell, id, productId)
      if (ask.tracking === "serial") {
        // Já tem séries no Odoo (outra receção, uma ronda, outra variante): nada a
        // perguntar — o servidor já passou o rastreio para as linhas; as séries seguem.
        const d = await getReceipt(shell, id)
        setDetail(d)
        shell.bridge.toast({ variant: "info", title: `${ask.product_name ?? "O artigo"} já tem número de série no Odoo`, description: "As séries seguem." })
        return continueAfter(d, queue, fromConfirm)
      }
      const serials = detail?.pending_serial_tracking?.find((p) => p.product_id === productId)?.serials ?? []
      setActivation({ ask: { ...ask, serial_count: serials.length || undefined }, queue, fromConfirm })
    } catch (e) {
      setError((e as Error).message)
    }
  }

  /** Um artigo ficou com séries: o próximo por decidir do «Confirmar», ou confirmar. */
  const continueAfter = async (d: ReceiptDetail, queue: number[], fromConfirm: boolean): Promise<void> => {
    const pending = (d.pending_serial_tracking ?? []).map((p) => p.product_id)
    const next = queue.find((pid) => pending.includes(pid))
    if (next) return openActivation(next, queue.filter((pid) => pid !== next), fromConfirm)
    if (fromConfirm && d.receipt.status === "pronto" && d.issues.length === 0) await confirm()
  }

  /**
   * Confirmar por passos: cada pedido faz um (verificar, fatura, receção de
   * cada encomenda, códigos, séries) e o ecrã repete até ao fim — um pedido
   * longo era cortado pelo proxy aos 30 s e deixava o documento preso.
   */
  const answerActivation = async (activate: boolean) => {
    const a = activation
    if (!a) return
    setActivationBusy(true)
    setActivationError(null)
    try {
      const d = await patchReceipt(shell, id, { serial_tracking: { product_id: a.ask.product_id, activate } })
      setDetail(d)
      setActivation(null)
      // Ficou ativado (ou já tinha séries no Odoo): segue. Depois de «Seguir sem
      // séries no Odoo» o fluxo NÃO continua sozinho — volta ao detalhe.
      if (d.receipt.write_log_json?.serial_tracking?.[String(a.ask.product_id)]?.decision === "ativado") await continueAfter(d, a.queue, a.fromConfirm)
    } catch (e) {
      setActivationError((e as Error).message)
    } finally {
      setActivationBusy(false)
    }
  }

  const confirm = async () => {
    setBusy(true)
    setError(null)
    try {
      for (let i = 0; i < 20; i++) {
        const p = await confirmStep(shell, id)
        setSteps(p.steps)
        const status = p.receipt.status
        if (p.done || p.step === null || status === "erro" || status === "erro_escrita" || status === "a_rever") break
      }
    } catch (e) {
      setError((e as Error).message)
    } finally {
      await load()
      setBusy(false)
    }
  }

  /** «Validar outra vez» (0.42.0): a receção que o Odoo não validou, numa receção escrita. */
  const retryStock = async () => {
    setBusy(true)
    setError(null)
    try {
      const p = await confirmStep(shell, id, { retryStock: true })
      setSteps(p.steps)
      const left = pendingStock(p.receipt)
      if (left.length === 0 && p.receipt.status === "escrito") shell.bridge.toast({ variant: "success", title: "Receção validada no Odoo" })
      else if (left.length > 0) shell.bridge.toast({ variant: "info", title: "Ainda não validada", description: left[0]!.message ?? undefined })
    } catch (e) {
      setError((e as Error).message)
    } finally {
      await load()
      setBusy(false)
    }
  }

  /** O resultado de «Criar e confirmar» / «Atualizar», num aviso. */
  const announceOrder = (out: CreatedOrder) => {
    const name = out.order.name ?? ""
    if (out.currency_hold) {
      shell.bridge.toast({
        variant: "info",
        title: `Encomenda ${name} ${out.order.created ? "criada" : "em rascunho"} — moeda diferente`,
        description: "Confirme-a no Odoo e depois carregue em «Atualizar».",
      })
      return
    }
    if (out.awaiting_approval) {
      shell.bridge.toast({
        variant: "info",
        title: `Encomenda ${name} ${out.order.created ? "criada" : "em rascunho"} — acima do limite de aprovação`,
        description: "Aprove-a no Odoo e depois carregue em «Atualizar».",
      })
      return
    }
    shell.bridge.toast({
      variant: out.unlinked.length ? "info" : "success",
      title: out.order.created ? `Encomenda ${name} criada e confirmada no Odoo` : out.order.confirmed_now ? `Encomenda ${name} confirmada no Odoo` : `Encomenda ${name} ligada à receção`,
      description: out.unlinked.length
        ? `Sem quantidade na encomenda para ${out.unlinked.length === 1 ? "a linha" : "as linhas"} ${out.unlinked.map((i) => i + 1).join(", ")}.`
        : out.order.created
          ? "A receção no Odoo foi gerada; o fluxo da receção segue."
          : "Não se criou outra encomenda.",
    })
  }

  /**
   * «Atualizar» (à espera de aprovação ou de moeda): relê o plano e só liga (ou
   * confirma) a MESMA encomenda; se foi rejeitada ou apagada no Odoo, diz-o —
   * nunca cria outra sem o diálogo.
   */
  const refreshApproval = async () => {
    const waiting = receipt.write_log_json?.order_creations?.at(-1)
    setBusy(true)
    setError(null)
    try {
      const plan = await getOrderPlan(shell, id)
      const decision = refreshDecision(plan, { order_id: waiting?.order_id ?? null, order_name: waiting?.order_name ?? null })
      if (decision.kind === "error") setError(decision.message)
      else announceOrder(await createPurchaseOrder(shell, id, { fingerprint: plan.fingerprint }))
    } catch (e) {
      setError((e as Error).message)
    } finally {
      await load()
      setBusy(false)
    }
  }

  const wide = useMinWidth(1280)
  const mid = useMinWidth(1024)
  const phone = !useMinWidth(768)
  const [tab, setTab] = useState<Tab>("linhas")
  const [checking, setChecking] = useState(false)
  const [highlight, setHighlight] = useState<number | null>(null)
  const [docNumber, setDocNumber] = useState<string | null>(null)
  /** Vencimento e condições de pagamento (0.43.0): rascunhos, como o número. */
  const [dueDate, setDueDate] = useState<string | null>(null)
  const [termsText, setTermsText] = useState<string | null>(null)
  const [completing, setCompleting] = useState(false)
  const [discarding, setDiscarding] = useState(false)
  const [menu, setMenu] = useState(false)
  /** «Criar encomenda a partir do documento» (0.36.0): o diálogo de confirmação. */
  const [ordering, setOrdering] = useState(false)
  /** «Escolher fornecedor» (0.38.0). */
  const [pickingSupplier, setPickingSupplier] = useState(false)
  const supplierSearch = useCallback((q: string) => searchSuppliers(shell, id, q), [shell, id])
  const catalogSearch = useCallback((q: string) => searchCatalog(shell, id, q), [shell, id])

  // Caixas conferidas: cada leitura grava-se logo; sem rede fica no telemóvel (L4).
  // Um envio de cada vez (leituras do mesmo frame em fila); a resposta traz só
  // as caixas conferidas, que se juntam ao detalhe sem o reler (correção 10).
  const boxQueue = usePendingQueue<{ serial: string }, { box_checks_json: BoxChecks }>(
    `maisenergia.receipt.${id}.boxes`,
    (p) => checkBox(shell, id, p.serial).catch((e) => Promise.reject(toRequestError(e))),
    (r) => setDetail((d) => (d ? { ...d, receipt: { ...d.receipt, box_checks_json: r.box_checks_json } } : d)),
    { serial: true },
  )

  // O número do documento edita-se como rascunho: «Guardar» aparece quando mudou.
  useEffect(() => setDocNumber(null), [detail?.receipt.document_number])
  useEffect(() => {
    setDueDate(null)
    setTermsText(null)
  }, [detail?.payment_terms?.due_date, detail?.payment_terms?.terms])

  if (!detail) {
    return <div className="p-4 text-base text-muted-foreground">{error ?? "A carregar…"}</div>
  }
  const { receipt, lines, issues } = detail
  const notices = detail.notices ?? []
  const editable = ["a_rever", "pronto", "erro"].includes(receipt.status)
  // Artigos sem número de série no Odoo com séries por decidir: o «sem rastreio» deles
  // resolve-se no aviso, que o botão de confirmar abre (não bloqueia o botão).
  const pendingSerial = editable ? (detail.pending_serial_tracking ?? []) : []
  const pendingLines = new Set(pendingSerial.flatMap((p) => p.line_indexes))
  const productOfLine = (lineIndex: number | null) => pendingSerial.find((p) => lineIndex !== null && p.line_indexes.includes(lineIndex))
  const blockingIssues = issues.filter((i) => !(i.kind === "not_tracked" && i.line_index !== null && pendingLines.has(i.line_index)))
  const canConfirm =
    (receipt.status === "pronto" || receipt.status === "erro" || (receipt.status === "a_rever" && pendingSerial.length > 0)) && blockingIssues.length === 0
  const onConfirm = () =>
    pendingSerial.length > 0
      ? void openActivation(pendingSerial[0]!.product_id, pendingSerial.slice(1).map((p) => p.product_id), true)
      : void confirm()
  // A meio da escrita (pedido cortado, erro depois de escrever): só continuar.
  const canContinue = receipt.status === "confirmado" || receipt.status === "erro_escrita"
  // Descartar: só uma receção sem nada escrito no Odoo (criada por engano). O «verificar» não escreve.
  const canDiscard =
    ["a_extrair", "a_rever", "pronto", "erro", "duplicado", "confirmado"].includes(receipt.status) &&
    !(receipt.write_log_json?.steps ?? []).some((s) => s !== "verificar")
  const shownSteps = steps ?? detail.steps
  const verified = new Set([...Object.keys(receipt.box_checks_json?.verified ?? {}), ...boxQueue.items.map((p) => p.payload.serial)])
  const docSerials = lines.flatMap((l) => l.serials)
  const boxTotal = docSerials.length
  const boxDone = docSerials.filter((s) => verified.has(s)).length
  const docDirty = docNumber !== null && docNumber.trim() !== (receipt.document_number ?? "")
  const terms = detail.payment_terms ?? { due_date: null, terms: null, days: null, mismatch: false }
  const dueDirty = dueDate !== null && (dueDate || null) !== terms.due_date
  const termsDirty = termsText !== null && (termsText.trim() || null) !== terms.terms
  const dirty = docDirty || dueDirty || termsDirty
  // Vencimento e condições: só numa fatura (uma guia não se paga).
  const showTerms = detail.document_kind !== "guide"
  const typeLabel = documentTypeLabel(receipt)
  const supplier = receipt.supplier_partner_name ?? receipt.supplier_name ?? "Fornecedor por ler"
  const canReprocess = editable || (receipt.status === "erro" && !receipt.extraction_status)
  const blockReason = !editable
    ? null
    : blockingIssues.length > 0
      ? `Resolva ${blockingIssues.length === 1 ? "o problema" : `os ${blockingIssues.length} problemas`} em «Antes de confirmar».`
      : receipt.status === "a_rever" && pendingSerial.length === 0
        ? "Ainda está a rever."
        : null

  /** «Antes de confirmar» → a linha (ou o cartão) a que o problema diz respeito. */
  const jump = (issue: ReviewIssue) => {
    const target =
      issue.line_index !== null && issue.kind !== "document_warning"
        ? `linha-${issue.line_index}`
        : /supplier/.test(issue.kind)
          ? "cartao-fornecedor"
          : "cartao-documento"
    if (!mid) setTab(target.startsWith("linha-") ? "linhas" : "resumo")
    if (issue.line_index !== null) setHighlight(issue.line_index)
    requestAnimationFrame(() => setTimeout(() => document.getElementById(target)?.scrollIntoView({ behavior: "smooth", block: "center" }), 60))
  }

  const issueActions = (issue: ReviewIssue): ReactNode => {
    if (!editable) return null
    const out: ReactNode[] = []
    const serialProduct = issue.kind === "not_tracked" ? productOfLine(issue.line_index) : undefined
    if (serialProduct) {
      out.push(
        <Button key="serie" variant="outline" className="h-11 bg-white sm:h-8" disabled={busy} onClick={() => void openActivation(serialProduct.product_id, [], false)}>
          Ativar o número de série…
        </Button>,
      )
    }
    if ((issue.kind === "line_no_order" || issue.kind === "no_order") && detail.order_draft?.offered) {
      out.push(
        <Button key="encomenda" variant="outline" className="h-auto min-h-11 whitespace-normal bg-white sm:min-h-8" disabled={busy} onClick={() => setOrdering(true)}>
          Criar encomenda a partir do documento…
        </Button>,
      )
    }
    if (issue.kind === "order_received" && issue.code) {
      const orderId = Number(issue.code.split(":")[1])
      out.push(
        <Button
          key="recebida"
          variant="outline"
          className="h-auto min-h-11 whitespace-normal bg-white sm:min-h-8"
          disabled={busy}
          onClick={() => patch({ received_in_odoo: { order_id: orderId, confirmed: true } })}
        >
          É dessa entrega — fechar sem stock
        </Button>,
      )
    }
    if (issue.kind === "supplier_unconfirmed" || issue.kind === "no_supplier") {
      out.push(
        <Button key="escolher" variant="outline" className="h-11 bg-white sm:h-8" disabled={busy} onClick={() => setPickingSupplier(true)}>
          Escolher fornecedor…
        </Button>,
      )
    }
    if (issue.kind === "supplier_unconfirmed" && detail.order_supplier) {
      out.push(
        <Button key="sup" variant="outline" className="h-auto min-h-11 whitespace-normal bg-white sm:min-h-8" disabled={busy} onClick={() => patch({ confirm_supplier: true })}>
          É {detail.order_supplier.name ?? "o fornecedor da encomenda"}
          {detail.order_supplier.vat ? ` (NIF ${detail.order_supplier.vat})` : ""}
        </Button>,
      )
    }
    if (issue.code && !NOT_DISMISSABLE.some((n) => issue.code === n || issue.code!.startsWith(`${n}:`))) {
      out.push(
        <Button key="ack" variant="outline" className="h-11 bg-white sm:h-8" disabled={busy} onClick={() => patch({ acknowledge: [issue.code] })}>
          Conferi no documento
        </Button>,
      )
    }
    return out.length ? out : null
  }

  /** «Conferir caixas» (L3): a app diz o que é cada código lido na caixa. */
  const onBoxCode = async (raw: string): Promise<ScanFeedback> => {
    const barcodes = new Map<string, string>()
    for (const l of lines) {
      for (const b of [l.odoo_barcode, ...l.barcode_candidates]) if (b) barcodes.set(b, l.odoo_product_name ?? l.description ?? "artigo")
    }
    const c = classifyCode(raw, { knownSerials: docSerials, productBarcodes: barcodes })
    if (c.kind === "serie_conhecida") {
      const line = lines.find((l) => l.serials.includes(c.code))
      const where = line ? ` · ${line.odoo_product_name ?? line.description ?? `linha ${line.line_index + 1}`}` : ""
      if (verified.has(c.code)) return { tone: "info", text: `Já conferida${where}.` }
      const out = await boxQueue.enqueue(c.code, { serial: c.code })
      if (!out.ok && out.queued) return { tone: "info", text: `Na fatura${where} — guardada no telemóvel, grava-se quando houver rede.` }
      if (!out.ok) return { tone: "error", text: out.error.message }
      const lineChecked = line ? line.serials.filter((s) => s === c.code || verified.has(s)).length : 0
      return { tone: "ok", text: `✓ Na fatura${where} · ${lineChecked} / ${line?.serials.length ?? 0} caixas conferidas` }
    }
    if (c.kind === "ean_artigo") return { tone: "info", text: `Código do produto (${c.product}) — leia a etiqueta com SN.` }
    if (c.kind === "nao_serie") return { tone: "error", text: "É um link ou texto (ex.: o QR do fabricante), não a série. Leia a etiqueta com SN." }
    if (c.kind === "parece_ean") {
      // E3: o EAN pode ir para o artigo da linha — só quando só há uma linha possível.
      const target = lines.filter(
        (l) => l.odoo_product_id && (l.match_method === "codigo" || l.match_method === "manual") && !l.odoo_barcode && !l.barcode_candidates.includes(c.code),
      )
      if (editable && target.length === 1) {
        const l = target[0]!
        return {
          tone: "warn",
          text: "Código do produto (EAN), não é uma série.",
          action: {
            label: `Gravar como código de barras de ${l.odoo_product_name ?? "este artigo"}`,
            run: async () => {
              setDetail(await patchReceipt(shell, id, { line: { line_index: l.line_index, barcode_candidate: c.code } }))
              return { tone: "ok", text: "Proposto como código de barras do artigo (grava-se ao confirmar)." }
            },
          },
        }
      }
      return { tone: "warn", text: "Código do produto (EAN), não é uma série. Leia a etiqueta com SN." }
    }
    // Só entre as séries ainda por conferir, e só com diferenças O/0, I/1, S/5, B/8, Z/2.
    const near = nearMatch(c.code, docSerials, verified)
    const nearLine = near ? lines.find((l) => l.serials.includes(near)) : undefined
    if (near && nearLine && editable) {
      return {
        tone: "warn",
        text: `Não está na fatura. Trocar ${near} por ${c.code}?`,
        action: {
          label: "Trocar",
          run: async () => {
            setDetail(await patchReceipt(shell, id, { box_swap: { line_index: nearLine.line_index, from: near, to: c.code } }))
            return { tone: "ok", text: `Trocada: ${c.code} na fatura · conferida.` }
          },
        },
      }
    }
    return { tone: "error", text: "Não está na fatura." }
  }

  const reprocess = async () => {
    if (await confirmDialog("Ler o documento outra vez? As correções feitas às linhas perdem-se.", { confirmLabel: "Reprocessar" })) void patch({ retry: "extract" })
  }
  const save = () => {
    const body: Record<string, unknown> = {}
    if (docDirty) body.document_number = docNumber
    if (dueDirty || termsDirty) {
      body.payment_terms = { ...(dueDirty ? { due_date: dueDate || null } : {}), ...(termsDirty ? { terms: termsText ?? "" } : {}) }
    }
    return Object.keys(body).length > 0 ? patch(body) : undefined
  }

  // ─── Blocos ───────────────────────────────────────────────────────────────

  const banners = (
    <>
      {receipt.status === "duplicado" && (
        <div className="space-y-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <p className="font-medium">Este documento já tinha sido recebido (mesmo fornecedor e número, ou mesmo ATCUD).</p>
          {detail.duplicate ? (
            <p>
              <Link to={`/armazem/${detail.duplicate.id}`} className="underline">
                Abrir a receção que o tem
              </Link>{" "}
              ({originLabel(detail.duplicate.origin) ?? "sem origem"}, {new Date(detail.duplicate.created_at).toLocaleString("pt-PT")} ·{" "}
              {STATUS_LABEL[detail.duplicate.status]}). Esta não segue.
            </p>
          ) : (
            <p>A receção original já não existe.</p>
          )}
          {shell.isAdmin ? (
            <>
              <p className="text-xs">Não é o mesmo documento (ex.: a fatura com o número da guia, ou o NIF escrito de outra forma)?</p>
              <Button variant="outline" className="h-auto min-h-12 whitespace-normal bg-white sm:min-h-9" disabled={busy} onClick={() => patch({ not_duplicate: true })}>
                Não é o mesmo documento — continuar esta receção
              </Button>
            </>
          ) : (
            <p className="text-xs">Se não for o mesmo documento, peça a um administrador para o separar.</p>
          )}
        </div>
      )}
      {(receipt.linked_deliveries_json ?? []).length > 0 && (
        <div className="space-y-1 text-xs text-muted-foreground">
          <p>Este documento voltou a chegar (não se criou outra receção):</p>
          {receipt.linked_deliveries_json.map((d, i) => (
            <div key={`${d.case_id ?? d.receipt_id ?? i}`} className="flex flex-wrap items-center gap-2">
              <span>
                {originLabel(d.origin, d.detail) ?? "?"}
                {d.case_number ? ` (caso ${d.case_number})` : ""} em {new Date(d.at).toLocaleString("pt-PT")}
              </span>
              {d.receipt_id && (
                <Link to={`/armazem/${d.receipt_id}`} className="underline">
                  ver
                </Link>
              )}
              {shell.isAdmin && d.origin === "email" && d.case_id != null && d.extraction_id && (
                <Button size="sm" variant="ghost" className="h-12 px-2 text-xs sm:h-6" disabled={busy} onClick={() => patch({ split_delivery: d.case_id })}>
                  Não é o mesmo — criar receção à parte
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
      {receipt.status === "escrito" && <WrittenSummary detail={detail} busy={busy} onRetryStock={() => void retryStock()} />}
      {receipt.status === "descartada" && receipt.discard_json && (
        <div className="space-y-1 rounded-xl border border-slate-300 bg-slate-50 p-3 text-sm">
          <p className="font-medium">
            Descartada em {new Date(receipt.discard_json.at).toLocaleString("pt-PT")}: {receipt.discard_json.reason}
          </p>
          <p className="text-xs text-muted-foreground">Nada foi escrito no Odoo.</p>
          {receipt.discard_json.case_closed && (
            <p className="text-xs text-muted-foreground">O caso {receipt.discard_json.case_closed.case_number ?? receipt.source_case_number} do inbox foi fechado com este motivo.</p>
          )}
          {receipt.discard_json.case_error && (
            <p className="text-xs text-amber-800">
              {receipt.discard_json.case_error}{" "}
              <button type="button" className="underline" onClick={() => setDiscarding(true)}>
                Tentar outra vez
              </button>
            </p>
          )}
        </div>
      )}
      {receipt.status === "concluida_manual" && receipt.manual_completion && (
        <div className="space-y-1 rounded-xl border border-slate-300 bg-slate-50 p-3 text-sm">
          <p className="font-medium">
            Concluída à mão por {detail.manual_completion_by ?? "um administrador"} em {new Date(receipt.manual_completion.at).toLocaleString("pt-PT")}:{" "}
            {receipt.manual_completion.note}
          </p>
          {receipt.manual_completion.steps_missing.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Não foram escritos pela app: {receipt.manual_completion.steps_missing.map((k) => stepLabel(detail, k)).join(", ")}.
            </p>
          )}
        </div>
      )}
      {(receipt.status === "erro" || receipt.status === "erro_escrita") && receipt.error && (
        <div className="space-y-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <p>{receipt.error}</p>
          {receipt.status === "erro_escrita" ? (
            <p className="text-xs">Já há escritas no Odoo: carregue em «Continuar a escrever» para retomar onde parou, sem duplicar.</p>
          ) : !receipt.extraction_status || receipt.extraction_status !== "completed" ? (
            <p className="text-xs">Use «Reprocessar» para ler o documento outra vez.</p>
          ) : (
            <p className="text-xs">Confirmar outra vez retoma onde parou, sem duplicar.</p>
          )}
        </div>
      )}
      {receipt.status === "a_extrair" && (
        <div className="flex items-center gap-2 rounded-xl border bg-sky-50 p-3 text-sm text-sky-900">
          <Loader2 className="h-4 w-4 animate-spin" /> A ler o documento — demora cerca de um minuto.
        </div>
      )}
    </>
  )

  // «Encomenda nr 94 - 015354» da Sanitop: lido como nossa encomenda, mas é o número do fornecedor.
  const supplierRefs = new Set(detail.supplier_order_refs ?? [])
  const ourOrders = (receipt.references_json.our_orders ?? []).filter((n) => !supplierRefs.has(n))
  const supplierOrders = [...supplierRefs, ...(receipt.references_json.supplier_orders ?? [])]
  const supplierIssue = issues.find((i) => i.kind === "supplier_unconfirmed" || i.kind === "no_supplier" || i.kind === "supplier_mismatch")
  const supplierCard = (
    <Panel id="cartao-fornecedor" className="scroll-mt-24">
      <CardTitle>Fornecedor</CardTitle>
      <div className="space-y-1 text-sm">
        <div className="text-base font-semibold">{supplier}</div>
        <div className="text-muted-foreground">
          {receipt.supplier_nif ? `NIF ${receipt.supplier_nif}` : "NIF não lido"}
          {receipt.supplier_name && receipt.supplier_partner_name && receipt.supplier_name !== receipt.supplier_partner_name && (
            <span> · no documento: {receipt.supplier_name}</span>
          )}
        </div>
        {receipt.status !== "a_extrair" &&
          (supplierIssue ? (
            <div className="mt-2 space-y-2 rounded-lg bg-amber-50 p-2.5 text-amber-900">
              <p className="flex items-start gap-1.5">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {supplierIssue.message}
              </p>
              {issueActions(supplierIssue)}
            </div>
          ) : receipt.supplier_partner_id ? (
            <p className="mt-1 flex items-center gap-1.5 text-emerald-700">
              <CheckCircle2 className="h-4 w-4" /> Fornecedor no Odoo
            </p>
          ) : null)}
        {receipt.supplier_partner_id && receipt.status !== "a_extrair" && (
          <p className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
            <span>{supplierSource(receipt)}</span>
            {editable && !receipt.guide_receipt_id && (
              <button type="button" className="min-h-11 underline sm:min-h-0" disabled={busy} onClick={() => setPickingSupplier(true)}>
                mudar
              </button>
            )}
          </p>
        )}
      </div>
    </Panel>
  )

  const documentCard = (
    <Panel id="cartao-documento" className="scroll-mt-24">
      <CardTitle>Documento</CardTitle>
      <div className="grid grid-cols-1 gap-x-4 gap-y-3 text-sm min-[420px]:grid-cols-2">
        <Field label="Tipo" value={typeLabel ?? receipt.document_type} />
        <div>
          <div className="text-xs text-muted-foreground">Número</div>
          {editable ? (
            <Input
              className="mt-0.5 h-12 text-base sm:h-9 sm:text-sm"
              placeholder="Número do documento"
              value={docNumber ?? receipt.document_number ?? ""}
              onChange={(e) => setDocNumber(e.target.value)}
            />
          ) : (
            <div className="font-medium">{receipt.document_number ?? "—"}</div>
          )}
        </div>
        <Field label="Data" value={receipt.document_date ? receipt.document_date.split("-").reverse().join("/") : null} />
        <Field label="ATCUD" value={receipt.atcud} />
        {showTerms && (
          <>
            <div>
              <div className="text-xs text-muted-foreground">Vencimento</div>
              {editable ? (
                <Input
                  type="date"
                  className="mt-0.5 h-12 text-base sm:h-9 sm:text-sm"
                  value={dueDate ?? terms.due_date ?? ""}
                  onChange={(e) => setDueDate(e.target.value)}
                />
              ) : (
                <div className="font-medium">{ptDate(terms.due_date) ?? "—"}</div>
              )}
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Condições de pagamento</div>
              {editable ? (
                <Input
                  className="mt-0.5 h-12 text-base sm:h-9 sm:text-sm"
                  placeholder="ex.: 60 dias"
                  value={termsText ?? terms.terms ?? ""}
                  onChange={(e) => setTermsText(e.target.value)}
                />
              ) : (
                <div className="font-medium">{terms.terms ?? "—"}</div>
              )}
              {!termsDirty && terms.terms && (
                <div className="text-xs text-muted-foreground">
                  {terms.days !== null ? `${terms.days} dias depois da data da fatura` : "Sem dias certos (fim do mês, prestações): vai só o vencimento"}
                </div>
              )}
            </div>
            {terms.mismatch && !dueDirty && !termsDirty && (
              <p className="flex items-start gap-1.5 text-xs text-amber-800 min-[420px]:col-span-2">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                O vencimento não bate com {terms.days} dias a contar da data do documento: na fatura vai a data de vencimento, sem condição.
              </p>
            )}
          </>
        )}
        <div className="min-[420px]:col-span-2">
          <Field
            label="Referências no documento"
            value={[...ourOrders, ...(receipt.references_json.delivery_notes ?? [])].join(" · ")}
          />
        </div>
        {supplierOrders.length > 0 && (
          <div className="min-[420px]:col-span-2">
            <Field label="Encomenda do fornecedor" value={supplierOrders.join(" · ")} hint="O número da encomenda no fornecedor — não é uma encomenda nossa no Odoo." />
          </div>
        )}
      </div>
      <div className="mt-4 border-t pt-3">
        <OrderPicker detail={detail} disabled={!editable || busy} onChange={(ids) => patch({ order_ids: ids })} onFind={(name) => patch({ find_order: name })} />
        {(detail.stock_preview ?? [])
          .filter((p) => p.received_in_odoo)
          .map((p) =>
            p.received_confirmed ? (
              <div key={p.order_id} className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-900">
                <span className="flex items-start gap-1.5">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                  {p.order_name ?? "A encomenda"} já recebida no Odoo ({p.received_in_odoo}): esta guia fecha sem dar entrada no stock.
                </span>
                {editable && (
                  <button
                    type="button"
                    className="min-h-11 text-xs underline sm:min-h-0"
                    disabled={busy}
                    onClick={() => patch({ received_in_odoo: { order_id: p.order_id, confirmed: false } })}
                  >
                    desfazer
                  </button>
                )}
              </div>
            ) : (
              <p key={p.order_id} className="mt-3 flex items-start gap-1.5 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                {p.order_name ?? "A encomenda"} já foi recebida no Odoo ({p.received_in_odoo}). Confirme em «Antes de confirmar» se esta guia é dessa entrega.
              </p>
            ),
          )}
        {detail.order_draft?.offered && (
          <div className="mt-3 rounded-lg border border-dashed border-amber-300 bg-amber-50/60 p-3 text-sm">
            <p className="text-amber-900">
              {detail.order_draft.lines.length > 0
                ? `${detail.order_draft.lines.length === 1 ? "Um artigo" : `${detail.order_draft.lines.length} artigos`} sem encomenda no Odoo.`
                : "Sem encomenda no Odoo para este documento."}{" "}
              Se a encomenda não existe, crie-a aqui com o documento como referência.
            </p>
            <Button variant="outline" className="mt-2 h-12 bg-white sm:h-8" disabled={busy} onClick={() => setOrdering(true)}>
              Criar encomenda a partir do documento…
            </Button>
          </div>
        )}
        <OrderTrail entries={receipt.write_log_json?.order_creations ?? []} />
        {editable && ["aguarda_aprovacao", "moeda_diferente"].includes(receipt.write_log_json?.order_creations?.at(-1)?.outcome ?? "") && (
          <div className="mt-2 flex flex-col gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900 sm:flex-row sm:items-center">
            <p className="flex-1">{receipt.write_log_json.order_creations?.at(-1)?.message}</p>
            <Button variant="outline" className="h-12 bg-white sm:h-8" disabled={busy} onClick={() => void refreshApproval()}>
              <RefreshCw className="h-4 w-4" /> Atualizar
            </Button>
          </div>
        )}
      </div>
      {editable && (detail.guide_candidates?.length > 0 || receipt.guide_receipt_id) && (
        <div className="mt-4 space-y-2 rounded-lg bg-sky-50 p-3 text-sm text-sky-900">
          <p className="font-medium">Esta fatura é de uma guia já tratada?</p>
          <p className="text-xs">Se sim, não se mexe de novo na receção do Odoo: as séries têm de ser exatamente as da guia, e cria-se só a fatura.</p>
          <select
            className="h-12 w-full rounded-md border bg-background px-2 text-base sm:h-9 sm:text-sm"
            value={receipt.guide_receipt_id ?? ""}
            disabled={busy}
            onChange={(e) => patch({ guide_receipt_id: e.target.value || null })}
          >
            <option value="">Não — é uma receção normal</option>
            {detail.guide_candidates.map((g) => (
              <option key={g.id} value={g.id}>
                Guia {g.document_number ?? "?"} · {g.document_date ?? ""} · {g.order_names.join(", ")} · {g.serial_count} série(s)
              </option>
            ))}
          </select>
        </div>
      )}
    </Panel>
  )

  const lineProps = {
    detail,
    issues: issues.filter((i) => i.line_index !== null && i.kind !== "document_warning"),
    notices,
    editable: editable && !busy,
    verified,
    highlight,
    onMatch: (lineIndex: number, purchaseLineId: number | null) => patch({ line: { line_index: lineIndex, purchase_line_id: purchaseLineId } }),
    onMatchProduct: (lineIndex: number, productId: number) => patch({ line: { line_index: lineIndex, product_id: productId } }),
    searchCatalog: catalogSearch,
    onAccept: (lineIndex: number) => patch({ line: { line_index: lineIndex, accept: true } }),
    onSaveBarcode: (lineIndex: number, v: boolean) => patch({ line: { line_index: lineIndex, save_barcode: v } }),
    onSerials: (lineIndex: number, serials: string[]) => patch({ line: { line_index: lineIndex, serials } }),
    onKind: (lineIndex: number, kind: LineKind | null) => void patchKind(lineIndex, kind),
    onMisc: (lineIndex: number, on: boolean) => patch({ line: { line_index: lineIndex, misc: on } }),
    productCategories: () => productCategories(shell, id),
    createProduct: async (lineIndex: number, input: NewProductInput) => {
      const product = await createLineProduct(shell, id, lineIndex, input)
      shell.bridge.toast({
        variant: "success",
        title: product.existed ? `Já existia: ${product.display_name ?? product.default_code ?? "artigo"}` : `Artigo criado: ${product.display_name ?? product.default_code ?? ""}`,
        description: product.warnings.join(" ") || undefined,
      })
      await load()
    },
  }
  const totals = (
    <div className="flex flex-wrap justify-end gap-x-5 gap-y-1 text-sm">
      <span className="text-muted-foreground">
        Base <span className="font-medium tabular-nums text-foreground">{eur(receipt.totals_json.net)}</span>
      </span>
      <span className="text-muted-foreground">
        IVA <span className="font-medium tabular-nums text-foreground">{eur(receipt.totals_json.vat)}</span>
      </span>
      <span className="font-semibold">
        Total <span className="tabular-nums">{eur(receipt.totals_json.gross)}</span>
      </span>
    </div>
  )
  const serialDecisions = Object.values(receipt.write_log_json?.serial_tracking ?? {})
  const serialCard =
    pendingSerial.length > 0 || serialDecisions.length > 0 ? (
      <div className="mb-3 space-y-2">
        {pendingSerial.map((p) => (
          <div key={p.product_id} className="flex flex-col gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 sm:flex-row sm:items-center">
            <AlertTriangle className="hidden h-4 w-4 shrink-0 sm:block" />
            <p className="flex-1">
              <span className="font-semibold">{p.product_name ?? `Artigo ${p.product_id}`}</span> ainda não tem número de série no Odoo — {p.serials.length} série(s) na
              {p.line_indexes.length === 1 ? ` linha ${p.line_indexes[0]! + 1}` : `s linhas ${p.line_indexes.map((i) => i + 1).join(", ")}`}.
            </p>
            <Button variant="outline" className="h-11 bg-white sm:h-8" disabled={busy || !editable} onClick={() => void openActivation(p.product_id, [], false)}>
              Decidir…
            </Button>
          </div>
        ))}
        {serialDecisions.map((d) => (
          <div key={d.product_id} className="flex flex-col gap-2 rounded-lg bg-muted/60 p-3 text-sm text-muted-foreground sm:flex-row sm:items-center">
            <p className="flex-1">
              <span className="font-medium text-foreground">{d.product_name ?? `Artigo ${d.product_id}`}</span>
              {d.decision === "ativado" ? " · número de série ativado no Odoo" : " · segue sem séries no Odoo"}
              {d.by_name ? ` por ${d.by_name}` : ""} · {new Date(d.at).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
            </p>
            {d.decision === "sem_series" && editable && (
              <Button variant="outline" className="h-11 bg-white sm:h-8" disabled={busy} onClick={() => void openActivation(d.product_id, [], false)}>
                Ativar o número de série…
              </Button>
            )}
          </div>
        ))}
      </div>
    ) : null

  const linesCard = (
    <Panel className={cn(!mid && "border-0 bg-transparent p-0")}>
      {mid && <CardTitle right={<span className="text-sm tabular-nums text-muted-foreground">{lines.length}</span>}>Linhas</CardTitle>}
      {serialCard}
      {receipt.status === "a_extrair" ? (
        <p className="text-sm text-muted-foreground">As linhas aparecem quando o documento acabar de ser lido.</p>
      ) : phone ? (
        <LinesCards {...lineProps} />
      ) : (
        <LinesTable {...lineProps} />
      )}
      <div className="mt-3 space-y-3">
        <ChargesBlock detail={detail} isAdmin={shell.isAdmin === true} />
        {receipt.status !== "a_extrair" && totals}
      </div>
    </Panel>
  )

  // O botão de confirmar o fornecedor já está no cartão do fornecedor: aqui só «Conferi no documento».
  const beforeConfirm = (
    <BeforeConfirmCard issues={issues} editable={editable} onJump={jump} actions={(i) => (i.kind === "supplier_unconfirmed" ? null : issueActions(i))} />
  )
  const willWrite = <WillWriteCard detail={detail} steps={shownSteps} running={busy && (canConfirm || canContinue)} />
  const boxCard = (
    <BoxCheckCard
      done={boxDone}
      total={boxTotal}
      pending={boxQueue.items.length}
      corrections={receipt.box_checks_json?.corrections ?? []}
      editable={editable}
      onOpen={() => setChecking(true)}
    />
  )
  const validationStack = (compact: boolean) => (
    <div className={cn(compact ? "grid gap-3 md:grid-cols-2" : "space-y-3")}>
      {receipt.status !== "a_extrair" && <ValidationCard detail={detail} compact={compact} />}
      {beforeConfirm}
      {willWrite}
      {boxCard}
      <OriginCard detail={detail} />
    </div>
  )

  const doc = (fill: boolean) => (
    <DocumentPreview
      // Uma receção nova (navegar entre receções) começa sem os endereços da anterior.
      key={receipt.id}
      shell={shell}
      receiptId={receipt.id}
      fromEmail={receipt.origin === "email"}
      fileKey={receipt.document_file_key}
      mime={receipt.document_mime}
      fileKeys={receipt.file_keys}
      fill={fill}
    />
  )

  // ─── Ações ────────────────────────────────────────────────────────────────

  const primary: ReactNode = editable ? (
    <span title={blockReason ?? undefined} className="flex min-w-0 flex-1 sm:flex-none">
      <Button className="h-14 w-full text-base font-semibold sm:h-10 sm:text-sm" disabled={!canConfirm || busy} onClick={onConfirm}>
        {busy ? <Loader2 className="h-5 w-5 animate-spin sm:h-4 sm:w-4" /> : <Check className="h-5 w-5 sm:h-4 sm:w-4" />}
        {phone ? "Confirmar" : receipt.status === "erro" ? "Confirmar outra vez e escrever no Odoo" : "Confirmar e escrever no Odoo"}
      </Button>
    </span>
  ) : canContinue ? (
    <Button className="h-14 flex-1 text-base font-semibold sm:h-10 sm:flex-none sm:text-sm" disabled={busy} onClick={confirm}>
      {busy ? <Loader2 className="h-5 w-5 animate-spin sm:h-4 sm:w-4" /> : <RefreshCw className="h-5 w-5 sm:h-4 sm:w-4" />}
      Continuar a escrever
    </Button>
  ) : null
  const secondary: Array<{ key: string; label: string; icon: ReactNode; run: () => void; show: boolean }> = [
    { key: "guardar", label: "Guardar", icon: <Save className="h-4 w-4" />, run: () => void save(), show: dirty },
    { key: "conferir", label: `Conferir caixas (${boxDone}/${boxTotal})`, icon: <ScanBarcode className="h-4 w-4" />, run: () => setChecking(true), show: phone && editable && boxTotal > 0 },
    { key: "reprocessar", label: "Reprocessar", icon: <RefreshCw className="h-4 w-4" />, run: reprocess, show: canReprocess },
    { key: "concluir", label: "Dar por concluída", icon: <CheckCircle2 className="h-4 w-4" />, run: () => setCompleting(true), show: canContinue && shell.isAdmin === true },
    { key: "descartar", label: "Descartar", icon: <Trash2 className="h-4 w-4" />, run: () => setDiscarding(true), show: canDiscard },
  ]

  return (
    <div className="mx-auto max-w-[1600px] space-y-4">
      <div className="space-y-2">
        <Link to="/armazem" className="inline-flex h-11 items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground sm:h-auto">
          <ArrowLeft className="h-4 w-4" /> Receções
        </Link>
        <div className="flex flex-wrap items-center gap-2">
          {receipt.receipt_code && <span className="font-mono text-sm text-muted-foreground">{receipt.receipt_code}</span>}
          <Badge variant={STATUS_VARIANT[receipt.status]}>{STATUS_LABEL[receipt.status]}</Badge>
          {typeLabel && <Badge variant="outline">{typeLabel}</Badge>}
        </div>
        <div className="flex flex-wrap items-start gap-3">
          <h1 className="min-w-0 flex-1 text-xl font-semibold leading-tight tracking-tight sm:text-2xl">
            {supplier}
            {receipt.document_number && <span className="text-muted-foreground"> · {receipt.document_number}</span>}
          </h1>
          {!phone && (
            <div className="flex flex-wrap items-center gap-2">
              {secondary
                .filter((a) => a.show)
                .map((a) => (
                  <Button key={a.key} variant={a.key === "guardar" ? "secondary" : "outline"} className="h-10" disabled={busy} onClick={a.run}>
                    {a.icon}
                    {a.label}
                  </Button>
                ))}
              {primary}
            </div>
          )}
        </div>
        {!phone && blockReason && editable && <p className="text-right text-xs text-muted-foreground">{blockReason}</p>}
      </div>

      {banners}
      {error && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      {detail.odoo_warning && <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">{detail.odoo_warning}</p>}

      {wide ? (
        <div className="grid grid-cols-[minmax(0,2.3fr)_minmax(0,7fr)_minmax(0,2.4fr)] items-start gap-4 min-[1440px]:grid-cols-[minmax(0,3.4fr)_minmax(0,7fr)_minmax(0,2.4fr)]">
          <div className="sticky top-2">{doc(false)}</div>
          <div className="space-y-4">
            {supplierCard}
            {documentCard}
            {linesCard}
          </div>
          <div className="sticky top-2 max-h-[calc(100vh-1rem)] overflow-y-auto pb-2">{validationStack(false)}</div>
        </div>
      ) : mid ? (
        <div className="grid grid-cols-[minmax(0,3fr)_minmax(0,9fr)] items-start gap-4">
          <div className="sticky top-2">{doc(false)}</div>
          <div className="space-y-4">
            {validationStack(true)}
            {supplierCard}
            {documentCard}
            {linesCard}
          </div>
        </div>
      ) : (
        <>
          <Segmented
            value={tab}
            onChange={setTab}
            items={[
              { key: "linhas", label: `Linhas ${lines.length}` },
              { key: "documento", label: "Documento" },
              { key: "validacao", label: issues.length > 0 && editable ? `Validação ${issues.length}` : "Validação" },
              { key: "resumo", label: "Resumo" },
            ]}
          />
          {tab === "linhas" && (
            <div className="space-y-3">
              {editable && issues.length > 0 && (
                <button type="button" onClick={() => setTab("validacao")} className="flex min-h-12 w-full items-center gap-2 rounded-lg bg-amber-50 p-3 text-left text-sm text-amber-900">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span className="flex-1">
                    {issues.length} problema{issues.length === 1 ? "" : "s"} antes de confirmar — ver em Validação
                  </span>
                </button>
              )}
              {linesCard}
            </div>
          )}
          {tab === "documento" && doc(true)}
          {tab === "validacao" && validationStack(false)}
          {tab === "resumo" && (
            <div className="space-y-3">
              {supplierCard}
              {documentCard}
            </div>
          )}
        </>
      )}

      {phone && (primary || secondary.some((a) => a.show)) && (
        <>
          <ActionBarSpacer />
          <ActionBar className="max-w-7xl">
            {secondary.some((a) => a.show) && (
              <Button variant="outline" className="h-14 w-14 shrink-0 px-0" aria-label="Mais ações" onClick={() => setMenu(true)}>
                <MoreHorizontal className="h-6 w-6" />
              </Button>
            )}
            {editable && boxTotal > 0 && (
              <Button variant="outline" className="h-14 shrink-0 gap-2 px-3 text-base" onClick={() => setChecking(true)} aria-label="Conferir caixas">
                <ScanBarcode className="h-5 w-5" />
                <span className="tabular-nums text-muted-foreground">
                  {boxDone}/{boxTotal}
                </span>
              </Button>
            )}
            {primary}
          </ActionBar>
          <BottomSheet open={menu} onClose={() => setMenu(false)} title="Ações">
            <div className="grid gap-2">
              {secondary
                .filter((a) => a.show)
                .map((a) => (
                  <button
                    key={a.key}
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setMenu(false)
                      a.run()
                    }}
                    className="flex h-14 items-center gap-3 rounded-xl border px-4 text-base font-medium active:bg-muted"
                  >
                    {a.icon}
                    {a.label}
                  </button>
                ))}
              {blockReason && <p className="text-sm text-muted-foreground">{blockReason}</p>}
            </div>
          </BottomSheet>
        </>
      )}

      {checking && (
        <Scanner
          title="Conferir caixas"
          hint={supplier}
          counter={
            <span>
              {boxDone}
              <span className="text-white/60"> / {boxTotal}</span>
            </span>
          }
          onCode={(code) => onBoxCode(code)}
          onClose={() => {
            setChecking(false)
            void load()
          }}
        />
      )}

      {activation && (
        <SerialActivationDialog
          ask={activation.ask}
          context="rececao"
          busy={activationBusy}
          error={activationError}
          onConfirm={() => void answerActivation(true)}
          onDecline={() => void answerActivation(false)}
          onClose={() => setActivation(null)}
        />
      )}
      <CreateOrderDialog
        open={ordering}
        setOpen={setOrdering}
        loadPlan={() => getOrderPlan(shell, id)}
        onConfirm={async (plan, prices) => {
          announceOrder(await createPurchaseOrder(shell, id, { fingerprint: plan.fingerprint, prices }))
          await load()
        }}
      />
      <SupplierPickerDialog
        open={pickingSupplier}
        setOpen={setPickingSupplier}
        nifRead={receipt.supplier_nif}
        documentName={receipt.supplier_name}
        foundByNif={!!receipt.supplier_partner_id && !receipt.supplier_confirmation && !!receipt.supplier_nif}
        search={supplierSearch}
        onPick={async (partner, associate) => {
          const d = await patchReceipt(shell, id, { supplier: { partner_id: partner.id, associate } })
          setDetail(d)
          if (d.supplier_alias && !d.supplier_alias.saved) {
            shell.bridge.toast({ variant: "error", title: "O fornecedor ficou escolhido, mas o NIF não ficou associado", description: d.supplier_alias.error ?? undefined })
          } else if (d.supplier_alias?.saved) {
            shell.bridge.toast({ variant: "success", title: `NIF ${receipt.supplier_nif} associado a ${partner.name ?? "este fornecedor"}`, description: "O próximo documento com este NIF já sai com ele." })
          }
        }}
      />
      <DiscardDialog
        open={discarding}
        setOpen={setDiscarding}
        createdOrders={ordersToCancel(receipt.write_log_json?.order_creations ?? [])}
        refusedOrders={ordersRefused(receipt.write_log_json?.order_creations ?? [])}
        fromCase={receipt.source_case_number ?? null}
        retry={receipt.status === "descartada" ? (receipt.discard_json?.reason ?? null) : null}
        onDone={async (reason) => {
          await discardReceipt(shell, id, reason)
          await load()
        }}
      />

      {canContinue && shell.isAdmin && (
        <CompleteManually
          detail={detail}
          open={completing}
          setOpen={setCompleting}
          onDone={async (note) => {
            await completeReceipt(shell, id, note)
            await load()
          }}
        />
      )}
    </div>
  )
}

function Field({ label, value, hint }: { label: string; value: string | null | undefined; hint?: string }) {
  return (
    <div className="min-w-0">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="truncate font-medium">{value || "—"}</div>
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
    </div>
  )
}

/** Escolher a encomenda quando há mais de uma candidata (ou nenhuma escolhida). */
function OrderPicker({
  detail,
  disabled,
  onChange,
  onFind,
}: {
  detail: ReceiptDetail
  disabled: boolean
  onChange: (ids: number[]) => void
  onFind: (name: string) => void
}) {
  const { receipt } = detail
  const candidates = receipt.candidate_orders_json ?? []
  const [name, setName] = useState("")
  const finder = !disabled && (
    <form
      className="mt-2 flex gap-2"
      onSubmit={(e) => {
        e.preventDefault()
        if (name.trim()) onFind(name.trim())
      }}
    >
      <Input className="h-12 min-w-0 flex-1 text-base sm:h-8 sm:w-52 sm:flex-none sm:text-sm" placeholder="Nº da encomenda (P0…)" value={name} onChange={(e) => setName(e.target.value)} />
      <Button type="submit" variant="outline" className="h-12 shrink-0 sm:h-8">
        Procurar de novo
      </Button>
    </form>
  )
  if (candidates.length === 0) {
    return receipt.status === "a_extrair" ? null : (
      <div>
        <p className="text-xs text-amber-700">Nenhuma encomenda de compra encontrada para este fornecedor e documento.</p>
        {finder}
      </div>
    )
  }
  const chosen = new Set(receipt.order_ids ?? [])
  return (
    <div>
      <div className="mb-1 text-xs text-muted-foreground">
        Encomenda{candidates.length > 1 ? "s candidatas — escolha" : ""}
      </div>
      <div className="flex flex-wrap gap-2">
        {candidates.map((o) => {
          const on = chosen.has(o.id)
          return (
            <Button
              key={o.id}
              className="h-12 sm:h-8"
              variant={on ? "default" : "outline"}
              disabled={disabled}
              onClick={() => onChange(on ? [...chosen].filter((x) => x !== o.id) : [...chosen, o.id])}
            >
              {o.name}
              {o.partner_ref ? <span className="ml-1 opacity-70">· {o.partner_ref}</span> : null}
              {o.receipt_status === "full" ? <span className="ml-1 opacity-70">· já recebida</span> : null}
            </Button>
          )
        })}
      </div>
      {finder}
    </div>
  )
}

/** Depois de confirmar: o que ficou escrito no Odoo, e o que falta fazer lá. */
function WrittenSummary({ detail, busy, onRetryStock }: { detail: ReceiptDetail; busy: boolean; onRetryStock: () => void }) {
  const { receipt, lines } = detail
  const serials = lines.reduce((acc, l) => acc + l.serials.length, 0)
  const pickings = Object.values(receipt.write_log_json.pickings ?? {})
  const codes = Object.keys(receipt.write_log_json.codes ?? {}).length
  const stock = Object.values(receipt.write_log_json.stock ?? {})
  const notValidated = pendingStock(receipt)
  // Antes da 0.42.0 (sem o passo do stock) a receção era validada à mão no Odoo; numa fatura de uma guia, foi a da guia.
  // Guia de encomendas já recebidas no Odoo (confirmado): não há receção a validar.
  const receivedInOdoo = receipt.received_in_odoo_json?.order_ids ?? []
  const validatedByHand = stock.length === 0 && !receipt.guide_receipt_id && receivedInOdoo.length === 0
  return (
    <Card className="border-emerald-200 bg-emerald-50">
      <CardContent className="space-y-2 p-4 text-sm text-emerald-900">
        <div className="flex items-center gap-2 font-medium">
          <CheckCircle2 className="h-5 w-5" /> Escrito no Odoo
        </div>
        <ul className="list-disc space-y-0.5 pl-5">
          {receivedInOdoo.length > 0 && (
            <li>
              {receivedInOdoo.map((id) => receipt.order_names?.[(receipt.order_ids ?? []).indexOf(id)] ?? id).join(", ")} já estava recebida no Odoo: sem entrada no stock por esta
              guia.
            </li>
          )}
          {receipt.vendor_bill_id && <li>Fatura de fornecedor {receipt.document_number} em rascunho (id {receipt.vendor_bill_id}), com o documento anexo.</li>}
          {billTermsText(receipt.write_log_json.bill) && <li>{billTermsText(receipt.write_log_json.bill)}.</li>}
          {receipt.write_log_json.bill?.accounting_date_message && (
            <li className="text-amber-800">{receipt.write_log_json.bill.accounting_date_message}</li>
          )}
          {(() => {
            const misc = Object.values(receipt.write_log_json.manual_received ?? {})
            const written = misc.filter((e) => e.status === "escrito").length
            const failed = misc.find((e) => e.status === "falhou")
            return (
              <>
                {written > 0 && <li>«Recebido» de {written} linha(s) Diversos na encomenda.</li>}
                {failed && <li className="text-amber-800">{failed.message}</li>}
              </>
            )
          })()}
          {Object.values(receipt.write_log_json.partner_refs ?? {})
            .filter((e) => e.status === "escrita")
            .map((e, i) => (
              <li key={`r${i}`}>
                Referência do fornecedor na encomenda {e.order_name ?? ""}: «{e.after}».
              </li>
            ))}
          {Object.values(receipt.write_log_json.partner_refs ?? {})
            .filter((e) => e.status === "recusada")
            .map((e, i) => (
              <li key={`rr${i}`} className="text-amber-800">
                {e.message}
              </li>
            ))}
          {pickings.map((p) => (
            <li key={p.picking_id}>
              {p.created + p.already_present} série(s) postas na receção {stock.find((e) => e.picking_id === p.picking_id)?.picking_name ?? p.picking_id}.
            </li>
          ))}
          {stock
            .filter((e) => e.status === "validada")
            .map((e) => (
              <li key={`s${e.picking_id}`} className="font-medium">
                Receção {e.picking_name ?? e.picking_id} validada — o stock entrou
                {e.backorder ? `; entrega pendente ${e.backorder.name ?? e.backorder.id} criada com o resto` : ""}.
              </li>
            ))}
          {stock
            .filter((e) => e.status === "nada")
            .map((e, i) => (
              <li key={`n${i}`}>{e.message ?? "Nada a dar entrada no stock."}</li>
            ))}
          {codes > 0 && <li>{codes} código(s) do fornecedor gravados nos artigos.</li>}
          {Object.values(receipt.write_log_json.code_conflicts ?? {}).map((m, i) => (
            <li key={`c${i}`} className="text-amber-800">{m}</li>
          ))}
          {Object.values(receipt.write_log_json.barcodes ?? {}).length > 0 && (
            <li>Código(s) de barras gravados: {Object.values(receipt.write_log_json.barcodes ?? {}).join(", ")}.</li>
          )}
          {Object.values(receipt.write_log_json.barcode_conflicts ?? {}).map((m, i) => (
            <li key={`b${i}`} className="text-amber-800">{m}</li>
          ))}
          {receipt.write_log_json.attachment_error && (
            <li className="text-amber-800">O documento não ficou anexo à fatura: {receipt.write_log_json.attachment_error}</li>
          )}
          {serials > 0 && <li>{serials} série(s) guardadas na app.</li>}
        </ul>
        {notValidated.map((e) => (
          <div key={`p${e.picking_id}`} className="space-y-2 rounded border border-amber-300 bg-amber-50 p-2 text-amber-900">
            <div className="flex items-start gap-2 font-medium">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                A receção {e.picking_name ?? e.picking_id ?? ""} não foi validada: {e.message ?? "o Odoo não a validou."}
                <span className="block text-xs font-normal">
                  {e.prepared ? "As quantidades do documento já estão na receção. " : ""}Valide-a no Odoo, ou carregue em «Validar outra vez».
                </span>
              </span>
            </div>
            <Button size="sm" variant="outline" className="h-11 sm:h-8" disabled={busy} onClick={onRetryStock}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Validar outra vez
            </Button>
          </div>
        ))}
        {validatedByHand && (
          <div className="flex items-start gap-2 rounded border border-amber-300 bg-amber-50 p-2 font-medium text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Falta validar a receção no Odoo. É o armazém que a valida, depois de conferir o material; a fatura publica-a a contabilidade.</span>
          </div>
        )}
        {!validatedByHand && receipt.vendor_bill_id && <p className="text-xs">A fatura fica em rascunho: publica-a a contabilidade.</p>}
      </CardContent>
    </Card>
  )
}

function stepLabel(detail: ReceiptDetail, key: string): string {
  return detail.steps.find((s) => s.key === key)?.label ?? key
}

/**
 * «Dar por concluída» (só admins): para uma receção presa com um erro
 * permanente. Mostra o que foi e o que não foi escrito e pede a nota do que se
 * fez à mão no Odoo. A app não escreve nada no Odoo.
 */
function CompleteManually({
  detail,
  open,
  setOpen,
  onDone,
}: {
  detail: ReceiptDetail
  open: boolean
  setOpen: (open: boolean) => void
  onDone: (note: string) => Promise<void>
}) {
  const [note, setNote] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dar por concluída</DialogTitle>
            <DialogDescription>
              A app não escreve mais nada no Odoo. Explique o que foi feito à mão.
            </DialogDescription>
          </DialogHeader>
          <ul className="space-y-1 text-sm">
            {detail.steps.map((s) => (
              <li key={s.key} className="flex items-center gap-2">
                {s.done ? <Check className="h-4 w-4 text-emerald-600" /> : <X className="h-4 w-4 text-red-600" />}
                <span className={s.done ? "" : "text-muted-foreground"}>
                  {s.label} {s.done ? "— escrito" : "— não escrito"}
                </span>
              </li>
            ))}
          </ul>
          <textarea
            className="min-h-24 w-full rounded-md border bg-background p-2 text-sm"
            placeholder="Ex.: receção validada à mão no Odoo com as séries da fatura."
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          {error && <p className="text-sm text-red-600">{error}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button
              disabled={busy || note.trim().length < 10}
              onClick={async () => {
                setBusy(true)
                setError(null)
                try {
                  await onDone(note.trim())
                  setOpen(false)
                } catch (e) {
                  setError((e as Error).message)
                } finally {
                  setBusy(false)
                }
              }}
            >
              Dar por concluída
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

/**
 * «Descartar» uma receção criada por engano: só sem nada escrito no Odoo, com
 * o motivo. Vinda de um email, o caso do inbox fecha com o motivo.
 */
/**
 * «Escolher fornecedor» (0.38.0): procura no Odoo pelo nome ou pelo NIF
 * (fornecedores e empresas primeiro) e, com um NIF lido, «Associar este NIF a
 * <fornecedor>» (ligado por omissão) — guardado na app, o parceiro do Odoo não muda.
 */
function SupplierPickerDialog({
  open,
  setOpen,
  nifRead,
  documentName,
  foundByNif,
  search,
  onPick,
}: {
  open: boolean
  setOpen: (open: boolean) => void
  nifRead: string | null
  documentName: string | null
  /** O fornecedor atual saiu do NIF lido no Odoo: mudar só se a leitura estiver errada. */
  foundByNif: boolean
  search: (q: string) => Promise<SupplierOption[]>
  onPick: (partner: SupplierOption, associate: boolean) => Promise<void>
}) {
  const [q, setQ] = useState("")
  const [results, setResults] = useState<SupplierOption[]>([])
  const [loading, setLoading] = useState(false)
  const [chosen, setChosen] = useState<SupplierOption | null>(null)
  const [associate, setAssociate] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) return
    // A primeira palavra do nome no documento («Amara, S.A.» → «Amara»): o nome inteiro raramente é o do Odoo.
    setQ((documentName ?? "").split(/[\s,.;/]+/).find((w) => w.length >= 3) ?? nifRead ?? "")
    setChosen(null)
    setAssociate(true)
    setError(null)
  }, [open, documentName, nifRead])

  useEffect(() => {
    if (!open || q.trim().length < 2) {
      setResults([])
      return
    }
    let alive = true
    setLoading(true)
    const t = setTimeout(() => {
      search(q.trim())
        .then((r) => alive && setResults(r))
        .catch((e: Error) => alive && setError(e.message))
        .finally(() => alive && setLoading(false))
    }, 300)
    return () => {
      alive = false
      clearTimeout(t)
    }
  }, [open, q, search])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Escolher fornecedor</DialogTitle>
          <DialogDescription>
            {nifRead ? `No documento: ${documentName ?? "—"} · NIF ${nifRead}.` : `No documento: ${documentName ?? "—"} (NIF não lido).`} Procure no Odoo pelo nome ou pelo NIF.
          </DialogDescription>
        </DialogHeader>
        {foundByNif && (
          <p className="rounded-md bg-amber-50 p-2 text-sm text-amber-900">O fornecedor atual corresponde ao NIF lido no Odoo. Mude só se a leitura estiver errada.</p>
        )}
        <Input
          autoFocus
          className="h-12 text-base sm:h-9 sm:text-sm"
          placeholder="Nome ou NIF"
          aria-label="Procurar fornecedor"
          value={q}
          onChange={(e) => {
            setQ(e.target.value)
            setChosen(null)
          }}
        />
        <ul className="max-h-72 space-y-1 overflow-y-auto" aria-label="Fornecedores encontrados">
          {loading && results.length === 0 && <li className="p-2 text-sm text-muted-foreground">A procurar…</li>}
          {!loading && q.trim().length >= 2 && results.length === 0 && <li className="p-2 text-sm text-muted-foreground">Nada encontrado no Odoo.</li>}
          {results.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => setChosen(p)}
                className={cn(
                  "flex min-h-12 w-full flex-col items-start rounded-lg border px-3 py-2 text-left text-sm hover:bg-muted sm:min-h-0",
                  chosen?.id === p.id && "border-sky-500 bg-sky-50 ring-1 ring-sky-500",
                )}
              >
                <span className="font-medium">{p.name ?? `Parceiro ${p.id}`}</span>
                <span className="text-xs text-muted-foreground">
                  {[p.vat ? `NIF ${p.vat}` : "sem NIF", p.supplier_rank > 0 ? "fornecedor" : null, !p.is_company ? "particular" : null, p.city, p.country]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </button>
            </li>
          ))}
        </ul>
        {nifRead && chosen && (
          <label className="flex min-h-11 items-start gap-2 text-sm sm:min-h-0">
            <input type="checkbox" className="mt-0.5 h-5 w-5 shrink-0 sm:h-4 sm:w-4" checked={associate} onChange={(e) => setAssociate(e.target.checked)} />
            <span>
              Associar este NIF ({nifRead}) a {chosen.name ?? "este fornecedor"}
              <span className="block text-xs text-muted-foreground">O próximo documento com este NIF já sai com este fornecedor. O NIF do fornecedor no Odoo não muda.</span>
            </span>
          </label>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancelar
          </Button>
          <Button
            disabled={!chosen || busy}
            onClick={async () => {
              if (!chosen) return
              setBusy(true)
              setError(null)
              try {
                await onPick(chosen, associate && !!nifRead)
                setOpen(false)
              } catch (e) {
                setError((e as Error).message)
              } finally {
                setBusy(false)
              }
            }}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Escolher
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function DiscardDialog({
  open,
  setOpen,
  fromCase,
  retry,
  createdOrders = [],
  refusedOrders = [],
  onDone,
}: {
  open: boolean
  setOpen: (open: boolean) => void
  fromCase: string | null
  /** Encomendas que esta receção criou no Odoo (0.36.0): o descarte cancela-as. */
  createdOrders?: string[]
  /** Encomendas ligadas que vieram de outra via: o servidor recusa o descarte. */
  refusedOrders?: string[]
  /** Já descartada e o caso não fechou: repetir com o mesmo motivo. */
  retry: string | null
  onDone: (reason: string) => Promise<void>
}) {
  const [reason, setReason] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const value = retry ?? reason
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{retry ? "Fechar o caso do inbox" : "Descartar a receção"}</DialogTitle>
          <DialogDescription>
            {retry
              ? `Volta a tentar fechar o caso ${fromCase ?? ""} com o motivo do descarte.`
              : createdOrders.length > 0
                ? "Para uma receção criada por engano. Sai de «Por tratar»; continua em «Todas»."
                : "Para uma receção criada por engano. Nada foi escrito no Odoo e nada é escrito. Sai de «Por tratar»; continua em «Todas»."}
          </DialogDescription>
        </DialogHeader>
        {fromCase && !retry && <p className="text-sm">Veio do caso {fromCase} do inbox: o caso fecha com este motivo.</p>}
        {refusedOrders.length > 0 && !retry && (
          <p className="rounded-md bg-red-50 p-2 text-sm text-red-700">
            A receção está ligada à encomenda {refusedOrders.join(", ")}, que não foi criada por ela: o descarte vai ser recusado — use «Dar por concluída».
          </p>
        )}
        {createdOrders.length > 0 && refusedOrders.length === 0 && !retry && (
          <p className="rounded-md bg-amber-50 p-2 text-sm text-amber-900">
            Vai cancelar a encomenda {createdOrders.join(", ")} no Odoo (criada por esta receção). Se já houver material recebido, não se descarta: use «Dar por
            concluída».
          </p>
        )}
        {!retry && (
          <textarea
            className="min-h-24 w-full rounded-md border bg-background p-2 text-base sm:text-sm"
            placeholder="Ex.: fatura de outra empresa, enviada por engano."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            aria-label="Motivo"
          />
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            disabled={busy || value.trim().length < 5 || (refusedOrders.length > 0 && !retry)}
            onClick={async () => {
              setBusy(true)
              setError(null)
              try {
                await onDone(value.trim())
                setOpen(false)
              } catch (e) {
                setError((e as Error).message)
              } finally {
                setBusy(false)
              }
            }}
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {retry ? "Fechar o caso" : "Descartar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const ORDER_OUTCOME: Record<OrderCreation["outcome"], string> = {
  criada: "criada e confirmada no Odoo",
  confirmada: "confirmada no Odoo (estava em rascunho)",
  ligada: "já existia no Odoo com esta referência — ligada",
  criada_sem_confirmar: "criada no Odoo, por confirmar",
  confirmacao_falhou: "por confirmar no Odoo",
  aguarda_aprovacao: "à espera de aprovação no Odoo",
  moeda_diferente: "em rascunho no Odoo (moeda diferente)",
  falhou: "não criada",
  cancelada: "cancelada no Odoo",
}

/**
 * A regra do servidor (`cancelCreatedOrders`), para o diálogo avisar logo: as
 * encomendas desta receção ainda não canceladas. As criadas por ela (e os
 * rascunhos dela, `confirmacao_falhou`) cancelam-se; uma ligada (veio de
 * outra via) faz o descarte ser recusado.
 */
const OWN_OUTCOMES = ["criada", "confirmada", "criada_sem_confirmar", "confirmacao_falhou", "aguarda_aprovacao", "moeda_diferente"]
function openOrders(entries: OrderCreation[]): OrderCreation[] {
  const cancelled = new Set(entries.filter((e) => e.outcome === "cancelada").map((e) => e.order_id))
  return entries.filter((e) => e.order_id && !cancelled.has(e.order_id))
}
function ordersToCancel(entries: OrderCreation[]): string[] {
  return [...new Set(openOrders(entries).filter((e) => OWN_OUTCOMES.includes(e.outcome)).map((e) => e.order_name ?? String(e.order_id)))]
}
function ordersRefused(entries: OrderCreation[]): string[] {
  return [...new Set(openOrders(entries).filter((e) => e.outcome === "ligada").map((e) => e.order_name ?? String(e.order_id)))]
}

/** O rasto de «Criar encomenda a partir do documento»: quem, quando e o que ficou no Odoo. */
function OrderTrail({ entries }: { entries: OrderCreation[] }) {
  if (entries.length === 0) return null
  return (
    <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
      {entries.map((e, i) => (
        <li key={`${e.at}-${i}`} className={cn(["falhou", "criada_sem_confirmar", "confirmacao_falhou", "aguarda_aprovacao"].includes(e.outcome) && "text-amber-800")}>
          Encomenda {e.order_name ?? ""} {ORDER_OUTCOME[e.outcome]}
          {e.by_name ? ` por ${e.by_name}` : ""} · {new Date(e.at).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
          {e.message && ["falhou", "criada_sem_confirmar", "confirmacao_falhou"].includes(e.outcome) ? ` — ${e.message}` : ""}
          {e.unlinked?.length ? ` — ${e.message ?? ""}` : ""}
        </li>
      ))}
    </ul>
  )
}

const SOURCE_LABEL: Record<"documento" | "tabela" | "custo", (supplier: string | null) => string> = {
  documento: () => "do documento",
  tabela: (supplier) => `tabela ${supplier ? `da ${supplier}` : "do fornecedor"}`,
  custo: () => "custo do artigo",
}

/**
 * «Criar encomenda a partir do documento» (0.36.0): a confirmação antes de
 * escrever no Odoo. Lê o plano do servidor ao abrir — fornecedor, referência
 * (o número do documento), cada linha com o documento ao lado do artigo do
 * Odoo, o preço que vai e de onde vem (documento, tabela do fornecedor, custo
 * do artigo) ou o campo para o indicar, o que já existe no Odoo e as
 * encomendas por receber com estes artigos. Confirma com a impressão digital
 * do plano: se o servidor vir outra coisa, recusa (409) e o diálogo relê.
 */
function CreateOrderDialog({
  open,
  setOpen,
  loadPlan,
  onConfirm,
}: {
  open: boolean
  setOpen: (open: boolean) => void
  loadPlan: () => Promise<OrderPlan>
  onConfirm: (plan: OrderPlan, prices: Record<string, number>) => Promise<void>
}) {
  const [plan, setPlan] = useState<OrderPlan | null>(null)
  const [prices, setPrices] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const reload = useCallback(() => {
    setPlan(null)
    setError(null)
    loadPlan()
      .then((p) => {
        setPlan(p)
        setPrices(Object.fromEntries(p.lines.filter((l) => l.needs_price && l.suggested_price).map((l) => [l.key, String(l.suggested_price)])))
      })
      .catch((e: Error) => setError(e.message))
  }, [loadPlan])
  useEffect(() => {
    if (open) reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
  const priceOf = (key: string) => Number(String(prices[key] ?? "").replace(",", "."))
  const missing = plan ? plan.lines.filter((l) => !l.excluded && l.needs_price && plan.action === "create" && !(priceOf(l.key) > 0)) : []
  // Na moeda da encomenda (a do fornecedor), não sempre em euros.
  const money = (v: number) => {
    try {
      return v.toLocaleString("pt-PT", { style: "currency", currency: plan?.currency || "EUR" })
    } catch {
      return `${v.toLocaleString("pt-PT")} ${plan?.currency ?? ""}`.trim()
    }
  }
  const blocked = !plan || plan.blockers.length > 0 || missing.length > 0
  const supplier = plan?.partner_name ?? null
  return (
    <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="pr-6">Criar encomenda a partir do documento</DialogTitle>
          <DialogDescription>
            Cria a encomenda de compra no Odoo e confirma-a — o Odoo gera a receção. Depois a receção segue como sempre (séries, códigos do fornecedor, fatura em
            rascunho).
          </DialogDescription>
        </DialogHeader>
        {!plan && !error && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> A ver os preços e o que já existe no Odoo…
          </p>
        )}
        {plan && (
          <>
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
              <dt className="text-muted-foreground">Fornecedor</dt>
              <dd className="font-medium">{plan.partner_name ?? "—"}</dd>
              <dt className="text-muted-foreground">Referência do fornecedor</dt>
              <dd className="font-mono font-medium">{plan.partner_ref ?? "—"}</dd>
            </dl>
            {plan.action === "link" && plan.existing && (
              <p className="rounded-md bg-sky-50 p-2 text-sm text-sky-900">
                Já existe no Odoo a encomenda {plan.existing.name} com esta referência: liga-se a ela, não se cria outra.
              </p>
            )}
            {plan.action === "confirm" && plan.existing && (
              <p className="rounded-md bg-sky-50 p-2 text-sm text-sky-900">
                O rascunho {plan.existing.name}, criado por esta receção, é confirmado no Odoo — não se cria outro.
              </p>
            )}
            {plan.lines.length > 0 && (
              <div className="overflow-x-auto rounded-lg border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2">No documento</th>
                      <th className="px-3 py-2">No Odoo</th>
                      <th className="px-3 py-2 text-right">Qtd</th>
                      <th className="px-3 py-2 text-right">Preço</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.lines.map((l) => (
                      <tr key={l.key} className="border-t align-top">
                        <td className="px-3 py-2">
                          {l.doc.map((d) => (
                            <div key={d.line_index} className="leading-snug [overflow-wrap:anywhere]">
                              {d.description ?? "(sem descrição)"}
                              <div className="text-xs text-muted-foreground">
                                {d.supplier_code ? <span className="font-mono">{d.supplier_code}</span> : "sem código"} · linha {d.line_index + 1}
                              </div>
                            </div>
                          ))}
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-medium leading-snug [overflow-wrap:anywhere]">{l.product_name ?? `Artigo ${l.product_id}`}</div>
                          {l.purchase_uom_name && (
                            <div className="text-xs text-amber-800">
                              Vai em {l.uom_name}; a unidade de compra do artigo é {l.purchase_uom_name}.
                            </div>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                          {l.quantity.toLocaleString("pt-PT")}
                          {l.uom_name && <div className="text-xs text-muted-foreground">{l.uom_name}</div>}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {l.excluded ? (
                            <span className="text-xs text-amber-800">{l.excluded}</span>
                          ) : l.needs_price && plan.action === "create" ? (
                            <div className="flex flex-col items-end gap-1">
                              <Input
                                inputMode="decimal"
                                className="h-12 w-28 text-right text-base sm:h-8 sm:text-sm"
                                placeholder="Preço"
                                aria-label={`Preço de ${l.product_name ?? "artigo"}`}
                                value={prices[l.key] ?? ""}
                                onChange={(e) => setPrices((p) => ({ ...p, [l.key]: e.target.value }))}
                              />
                              <span className="text-xs text-amber-800">{l.suggested_price ? "sugestão do Odoo — confirme" : "sem preço no Odoo — indique"}</span>
                            </div>
                          ) : l.price_unit !== null ? (
                            <div className="whitespace-nowrap">
                              {money(l.price_unit)}
                              {l.discount ? <div className="text-xs text-muted-foreground">−{l.discount}%</div> : null}
                              {l.price_source && <div className="text-xs text-muted-foreground">{SOURCE_LABEL[l.price_source](supplier)}</div>}
                            </div>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {plan.currency_hold && (
              <p className="rounded-md bg-amber-50 p-2 text-sm text-amber-900">
                A encomenda fica em {plan.currency} — moeda diferente da da empresa ({plan.company_currency}): fica em rascunho para confirmar no Odoo (preços e
                câmbio) e depois «Atualizar».
              </p>
            )}
            {plan.pending_orders.length > 0 && (
              <ul className="space-y-1 rounded-md bg-amber-50 p-2 text-sm text-amber-900">
                {plan.pending_orders.map((o) => (
                  <li key={o.id} className="flex items-start gap-1.5">
                    <Info className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>
                      {o.name}
                      {o.partner_ref ? ` (${o.partner_ref})` : ""} está confirmada e tem por receber {o.products.join(", ")} — pode ser esta: escolha-a em «Encomenda».
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {plan.blockers.length > 0 && (
              <ul className="space-y-1 rounded-md bg-amber-50 p-2 text-sm text-amber-900">
                {plan.blockers.map((b) => (
                  <li key={b} className="flex items-start gap-1.5">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {b}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
        {error && <p className="rounded-md bg-red-50 p-2 text-sm text-red-700">{error}</p>}
        <DialogFooter className="gap-2">
          <Button variant="outline" className="h-12 sm:h-10" disabled={busy} onClick={() => setOpen(false)}>
            Cancelar
          </Button>
          <Button
            className="h-12 sm:h-10"
            disabled={busy || blocked}
            onClick={async () => {
              if (!plan) return
              setBusy(true)
              setError(null)
              try {
                const given = Object.fromEntries(plan.lines.filter((l) => l.needs_price && !l.excluded).map((l) => [l.key, priceOf(l.key)]))
                await onConfirm(plan, given)
                setOpen(false)
              } catch (e) {
                setError((e as Error).message)
                // O plano mudou (409) ou o Odoo recusou: relê para mostrar o que é agora.
                if (/mudaram/.test((e as Error).message)) reload()
              } finally {
                setBusy(false)
              }
            }}
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {plan?.action === "link" ? "Ligar a encomenda" : plan?.action === "confirm" ? "Confirmar no Odoo" : "Criar e confirmar no Odoo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
